// Respostas rápidas da loja (menu "/" do composer) e variáveis da loja.
//
// Tag `QuickReply` com id `STORE:<storeId>` para a lista, as variáveis E o contexto de
// uma conversa da loja: toda escrita no back emite `quick_replies.changed {storeId}`, e
// o listener do WS invalida só esta tag. Assim a lista do atendente muda sem recarregar.
//
// Nomes prefixados com `QuickReply` de propósito: o `api` base é único e dois endpoints
// com o mesmo nome colidem em silêncio (ver templates/api.ts).
import { api } from '@/lib/api';

export interface QuickReply {
  id: string;
  storeId: string;
  /** Atalho sem a barra: `^[a-z0-9_-]{1,30}$`, único na loja. */
  shortcut: string;
  text: string;
  version: number;
  updatedAt: string;
}

export interface QuickReplyVariable {
  key: string;
  value: string;
}

export interface QuickReplyVariablesResponse {
  data: QuickReplyVariable[];
  /** Chaves que o sistema preenche sozinho (nome_cliente, numero_pedido). */
  reserved: string[];
}

export interface CreateQuickReplyArgs {
  storeId: string;
  shortcut: string;
  text: string;
}

export interface UpdateQuickReplyArgs {
  storeId: string;
  id: string;
  shortcut?: string;
  text?: string;
  /** Lock otimista: versão velha devolve 409 STALE_ENTITY. */
  version: number;
}

export interface QuickReplyContextArgs {
  conversationId: string;
  /** Só para a tag: o contexto inclui as variáveis da loja. */
  storeId: string;
}

export const quickRepliesTag = (storeId: string) => ({
  type: 'QuickReply' as const,
  id: `STORE:${storeId}`,
});

export const quickRepliesApi = api.injectEndpoints({
  endpoints: (build) => ({
    listQuickReplies: build.query<QuickReply[], string>({
      query: (storeId) => `/stores/${storeId}/quick-replies`,
      transformResponse: (raw: { data: QuickReply[] }) => raw.data,
      providesTags: (_r, _e, storeId) => [quickRepliesTag(storeId)],
    }),
    createQuickReply: build.mutation<QuickReply, CreateQuickReplyArgs>({
      query: ({ storeId, ...body }) => ({
        url: `/stores/${storeId}/quick-replies`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [quickRepliesTag(arg.storeId)],
    }),
    updateQuickReply: build.mutation<QuickReply, UpdateQuickReplyArgs>({
      query: ({ storeId, id, ...body }) => ({
        url: `/stores/${storeId}/quick-replies/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [quickRepliesTag(arg.storeId)],
    }),
    deleteQuickReply: build.mutation<void, { storeId: string; id: string }>({
      query: ({ storeId, id }) => ({
        url: `/stores/${storeId}/quick-replies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [quickRepliesTag(arg.storeId)],
    }),
    listQuickReplyVariables: build.query<QuickReplyVariablesResponse, string>({
      query: (storeId) => `/stores/${storeId}/quick-reply-variables`,
      providesTags: (_r, _e, storeId) => [quickRepliesTag(storeId)],
    }),
    upsertQuickReplyVariable: build.mutation<
      QuickReplyVariable,
      { storeId: string; key: string; value: string }
    >({
      query: ({ storeId, key, value }) => ({
        url: `/stores/${storeId}/quick-reply-variables/${encodeURIComponent(key)}`,
        method: 'PUT',
        body: { value },
      }),
      invalidatesTags: (_r, _e, arg) => [quickRepliesTag(arg.storeId)],
    }),
    deleteQuickReplyVariable: build.mutation<void, { storeId: string; key: string }>({
      query: ({ storeId, key }) => ({
        url: `/stores/${storeId}/quick-reply-variables/${encodeURIComponent(key)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [quickRepliesTag(arg.storeId)],
    }),
    /** Valores das variáveis para UMA conversa (nome do cliente, último pedido, loja). */
    getQuickReplyContext: build.query<Record<string, string | null>, QuickReplyContextArgs>({
      query: ({ conversationId }) => `/conversations/${conversationId}/quick-reply-context`,
      transformResponse: (raw: { values: Record<string, string | null> }) => raw.values,
      providesTags: (_r, _e, arg) => [quickRepliesTag(arg.storeId)],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListQuickRepliesQuery,
  useCreateQuickReplyMutation,
  useUpdateQuickReplyMutation,
  useDeleteQuickReplyMutation,
  useListQuickReplyVariablesQuery,
  useUpsertQuickReplyVariableMutation,
  useDeleteQuickReplyVariableMutation,
  useLazyGetQuickReplyContextQuery,
} = quickRepliesApi;
